import { createApi } from '@reduxjs/toolkit/query/react';
import config from 'config/index';
import axiosBaseQuery from 'rtk/query/axiosBaseQuery';

import { toast } from 'sonner';

// types
import type { TOneOffEmailSendRequest } from 'components/Jobs/Job/JobTab/JobSettings/JobEmailSettings';
import { EUserType } from 'types/accessControll.types';
import type { TFactsJob, TFactsPatch } from 'types/factsJob.types';
import type { TInterview } from 'types/interview.types';
import type { TScoringJobRole } from 'types/job-roles.types';
import type { TJob } from 'types/job.types';
import type { TTemplate } from 'types/template/template.types';
import type { TPaginationResult } from '../api-common.types';
import type {
  TApplyToJobPayload,
  TApplyToJobResponse,
  TJobInterviewActionName,
  TPaginationJobScoreRequest,
  TPaginationJobsRequest,
  TSimplifiedJobApplicationsExportDataRow,
  TUpdateJobFields,
} from './jobs.api.types';

type TCanAccessJobResult = {
  job: TJob;
  templates: Pick<TTemplate, '_id' | 'name'>[];
  role: Pick<TScoringJobRole, 'role_id' | 'description'>;
};

type TCanAccessJobPayload = { jobId: string | undefined };

const reducerPath = 'jobs' as const;
const path = 'v1/jobs' as const;

export const jobsApi = createApi({
  reducerPath: reducerPath,
  baseQuery: axiosBaseQuery({
    baseUrl: `${config.VITE_BACKEND_URL}/${path}`,
  }),
  endpoints: (builder) => ({
    getJobById: builder.query<any, { jobId: string; include?: string[] }>({
      query: (payload) => {
        console.log('getJobById payload', payload);
        let url = `/${payload.jobId}`;
        url += payload.include?.length ? `?include=${payload.include.join(',')}` : '';
        return {
          url: url,
          method: 'GET',
          headers: { 'X-Access-Control': EUserType.ANY },
        };
      },
    }),

    canAccessJob: builder.query<TCanAccessJobResult, TCanAccessJobPayload>({
      // @ts-ignore
      query: (payload) => {
        if (!payload.jobId) {
          toast.error('No job ID provided');
          return;
        }

        return {
          url: `/${payload.jobId}/has-access`,
          method: 'GET',
          headers: { 'X-Access-Control': EUserType.ANY },
        };
      },
    }),

    exportBasicApplicationDataByJobId: builder.mutation<
      {
        columns: { header: string; field: keyof TSimplifiedJobApplicationsExportDataRow; type: string }[];
        exportString: string;
        exportArray: TSimplifiedJobApplicationsExportDataRow[];
      },
      { jobId: string }
    >({
      // @ts-ignore
      query: (payload) => {
        if (!payload.jobId) {
          toast.error('No job ID provided');
          return;
        }

        return {
          url: `/${payload.jobId}/export-basic-application-data`,
          method: 'GET',
          headers: { 'X-Access-Control': EUserType.HUBERT },
        };
      },
    }),

    findPaginatedJobs: builder.query<TPaginationResult<TJob>, TPaginationJobsRequest>({
      query: (payload) => {
        return {
          url: `/paginated-find`,
          method: 'POST',
          data: payload,
          headers: { 'X-Access-Control': EUserType.ANY },
        };
      },
    }),

    getJobTabStatistics: builder.query<any, any>({
      query: (jobId: string) => {
        console.log('getJobsTabStatistics jobId', jobId);
        return {
          url: `/${jobId}/job-tab-statistics`,
          method: 'GET',
          headers: { 'X-Access-Control': EUserType.ANY },
        };
      },
    }),

    getJobScoreTabCardStatistics: builder.query<any, any>({
      query: (jobId: string) => {
        console.log('getJobScoreTabStatistics jobId', jobId);
        return {
          url: `/${jobId}/job-score-card-statistics`,
          method: 'GET',
          headers: { 'X-Access-Control': EUserType.HUBERT },
        };
      },
    }),

    updateJobField: builder.mutation<
      { job: TJob },
      {
        jobId: string;
        fieldName: TUpdateJobFields;
        value: string | number | boolean;
      }
    >({
      query: (x) => {
        console.log('updateJobField jobId', x.jobId);
        return {
          url: `/${x.jobId}/update-field`,
          method: 'POST',
          data: x,
          headers: { 'X-Access-Control': EUserType.HUBERT },
        };
      },
    }),

    findPaginatedJobScoring: builder.query<TPaginationResult<TInterview>, TPaginationJobScoreRequest>({
      query: (payload) => {
        console.log('findPaginatedJobScoring payload', payload);
        return {
          url: `/${payload.filter.jobId}/paginated-scoring`,
          method: 'POST',
          data: payload,
          headers: { 'X-Access-Control': EUserType.HUBERT },
        };
      },
    }),

    sendOneOffEmail: builder.mutation<any, TOneOffEmailSendRequest>({
      query: (payload) => {
        console.log('sendOneOffEmail jobId', payload.jobId);
        return {
          url: `/${payload.jobId}/send-one-off-email`,
          method: 'POST',
          data: payload,
          headers: { 'X-Access-Control': EUserType.HUBERT },
        };
      },
    }),

    jobInterviewActions: builder.mutation<
      | {
          success: boolean;
          message: string;
          error?: any;
        }[]
      | {
          matchedCount: number;
          modifiedCount: number;
          message: string;
        },
      {
        actionName: TJobInterviewActionName;
        jobId: string;
        interviewIds: string[];
      }
    >({
      query: (x) => {
        console.log('jobInterviewActions jobId', x);
        return {
          url: `/${x.jobId}/interview-actions`,
          method: 'POST',
          data: x,
          headers: { 'X-Access-Control': EUserType.HUBERT },
        };
      },
    }),

    applyToJob: builder.mutation<{ data: TApplyToJobResponse }, TApplyToJobPayload>({
      query: (x) => {
        console.log('applyToJob jobId', x);
        return {
          url: `/${x.jobId}/apply`,
          method: 'POST',
          data: x,
          headers: { 'X-Access-Control': EUserType.HUBERT },
        };
      },
    }),

    getJobFacts: builder.query<TFactsJob | null, { jobId: string }>({
      query: ({ jobId }) => ({
        url: `/${jobId}/facts`,
        method: 'GET',
        headers: { 'X-Access-Control': EUserType.HUBERT },
      }),
    }),

    extractJobFacts: builder.mutation<TFactsJob | null, { jobId: string; adText: string }>({
      query: ({ jobId, adText }) => ({
        url: `/${jobId}/facts/extract`,
        method: 'POST',
        data: { adText },
        headers: { 'X-Access-Control': EUserType.HUBERT },
      }),
      async onQueryStarted({ jobId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(jobsApi.util.upsertQueryData('getJobFacts', { jobId }, data));
        } catch {
          // the caller shows the error
        }
      },
    }),

    updateJobFacts: builder.mutation<TFactsJob, { jobId: string; facts: TFactsPatch }>({
      query: ({ jobId, facts }) => ({
        url: `/${jobId}/facts`,
        method: 'PATCH',
        data: facts,
        headers: { 'X-Access-Control': EUserType.HUBERT },
      }),
      async onQueryStarted({ jobId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(jobsApi.util.upsertQueryData('getJobFacts', { jobId }, data));
        } catch {
          // the caller shows the error
        }
      },
    }),

    copyJobToTestDB: builder.mutation<any, { jobId: string }>({
      query: (payload) => {
        console.log('copyJobToTestDB jobId', payload.jobId);
        return {
          url: `/${payload.jobId}/copy-job-to-test-db`,
          method: 'GET',
          headers: {
            'X-Access-Control': EUserType.HUBERT,
          },
        };
      },
      // async onQueryStarted (
      //   jobId: string,
      //   { dispatch, queryFulfilled, getState }: any
      // ){
      //   try {
      //     const data = await queryFulfilled;
      //     let str = JSON.stringify(data.data.results, null, 2);
      //     str = PrettyPrintJson({ data: data.data.results })
      //     toast.success(str)
      //     console.log('copyJobToTestDB queryFulfilled data', data);
      //   } catch (error) {
      //     toast.error(`Something went wrong. ${error.message}`)
      //     console.log('ERROR: copyJobToTestDB: ', error.message)
      //   }
      // }
    }),
  }),
});

export const {
  // Queries

  useCanAccessJobQuery,
  useGetJobTabStatisticsQuery,

  // Job scoring tab query
  useFindPaginatedJobScoringQuery,

  useFindPaginatedJobsQuery,
  useGetJobFactsQuery,

  // Mutations
  useJobInterviewActionsMutation,
  useApplyToJobMutation,
  useCopyJobToTestDBMutation,
  useExtractJobFactsMutation,
  useUpdateJobFactsMutation,
  useUpdateJobFieldMutation,
  useExportBasicApplicationDataByJobIdMutation,
} = jobsApi;
